import React, { useCallback, useEffect, useRef } from 'react';
import cn from 'classnames';
import { useAtom } from 'jotai';
import { useAtomValue } from 'jotai/utils';
import {
  chatOpenAtom,
  chatMessagesAtom,
  chatStreamingAtom,
  chatSessionIdAtom,
  chatHistoryViewAtom,
  pendingChipAtom,
} from 'src/shared/chat';
import { chatMaximizedAtom } from 'src/shared/chatPreferences';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { useSaveChatSessionMutation } from 'src/api/mutations/chatMutations';
import { useIsMobile } from 'src/hooks/useIsMobile';
import { useFeatureAvailability } from 'src/hooks/useFeatureAvailability';
import { lockScroll, unlockScroll } from 'src/utils/bodyScroll';
import { truncateAtWord } from 'src/utils/truncate';
import { ChatAI } from 'src/common/icons';
import { useChatSend } from './useChatSend';
import { useChatSessionReset } from './useChatSessionReset';
import { ChatHeader } from './ChatHeader';
import { ChatInput } from './ChatInput';
import { MessageList } from './MessageList';
import { ChatHistory } from './ChatHistory';
import { BudgetIndicator, useBudgetAllowed } from './BudgetIndicator';
import styles from './ChatSidebar.module.scss';

// Threshold for the swipe-right close gesture on the chat header. We
// only treat the gesture as a "close" if the touch starts inside the
// top band of the panel (the header zone), travels at least this many
// pixels horizontally, and is predominantly horizontal rather than
// vertical (so a scroll inside the body of the panel never closes it).
const SWIPE_CLOSE_THRESHOLD_PX = 60;
const HEADER_ZONE_PX = 80;

export function useChatAutoSave() {
  const messages = useAtomValue(chatMessagesAtom);
  const [sessionId, setSessionId] = useAtom(chatSessionIdAtom);
  const isStreaming = useAtomValue(chatStreamingAtom);
  const saveMutation = useSaveChatSessionMutation();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedCountRef = useRef(0);

  /**
   * Saves right now instead of waiting out the debounce, and returns whether
   * it had anything to save.
   *
   * `assignSessionId: false` is for the caller that is about to drop the
   * session (starting a new one): the save still needs an id to file the
   * history entry under, but writing it back would resurrect the session we
   * are leaving.
   */
  const flushNow = useCallback(
    (assignSessionId = true) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (messages.length === 0) return false;
      if (messages.length === lastSavedCountRef.current) return false;

      const sid = sessionId || `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      if (!sessionId && assignSessionId) setSessionId(sid);

      const firstUserMsg = messages.find((m) => m.role === 'user');
      const title = (firstUserMsg && truncateAtWord(firstUserMsg.content, 60)) || 'Untitled chat';

      saveMutation.mutate({ session_id: sid, title, messages });
      lastSavedCountRef.current = messages.length;
      return true;
    },
    [messages, sessionId, setSessionId, saveMutation],
  );

  useEffect(() => {
    // The list got shorter, so this is a different conversation than the one
    // the counter describes — a new session, or one loaded from history.
    // Without this, the new conversation's Nth message is silently skipped
    // when N happens to equal the old conversation's saved length.
    if (messages.length < lastSavedCountRef.current) lastSavedCountRef.current = 0;

    if (isStreaming || messages.length === 0) return;
    if (messages.length === lastSavedCountRef.current) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      const sid = sessionId || `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      if (!sessionId) {
        setSessionId(sid);
      }

      const firstUserMsg = messages.find((m) => m.role === 'user');
      const title = (firstUserMsg && truncateAtWord(firstUserMsg.content, 60)) || 'Untitled chat';

      saveMutation.mutate({
        session_id: sid,
        title,
        messages,
      });

      lastSavedCountRef.current = messages.length;
    }, 2000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [messages, isStreaming, sessionId, setSessionId, saveMutation]);

  return { flushNow };
}

export const ChatSidebar: React.FC = () => {
  const [isOpen, setChatOpen] = useAtom(chatOpenAtom);
  const [isMaximized, setMaximized] = useAtom(chatMaximizedAtom);
  const [historyView, setHistoryView] = useAtom(chatHistoryViewAtom);
  const { send: sendChat, stop: stopChat } = useChatSend();
  const isMobile = useIsMobile();
  const { flushNow } = useChatAutoSave();
  // Drop the session id rather than reassigning it: we are about to null it.
  const flushSave = useCallback(() => {
    flushNow(false);
  }, [flushNow]);
  useChatSessionReset({ stopStream: stopChat, flushSave });

  // On mobile the panel covers the whole viewport, so pin the page
  // underneath to stop iOS Safari from rubber-banding through it.
  useEffect(() => {
    if (!(isMobile && isOpen)) return;
    lockScroll();
    return () => unlockScroll();
  }, [isMobile, isOpen]);

  // The category picker (rendered inside <MessageList /> for the empty
  // state) writes draft chips into pendingChipAtom; ChatInput reads
  // them. Keeping the producer + consumer decoupled lets us add new
  // chip producers later (curated context chips, prompt history,
  // schedule re-runs) without restructuring the sidebar.
  const [pendingChip, setPendingChip] = useAtom(pendingChipAtom);
  const budgetAllowed = useBudgetAllowed();
  // The AI Agent is a built-in feature shown to everyone. Render a read-only
  // "Not enabled" state when the account doesn't have the AI addon, or when
  // the saas-side AI APIs 404 on the old release during the rollout.
  const addonsSettings = useAtomValue(addonsSettingsAtom);
  const aiAddonEnabled = Boolean(
    addonsSettings?.AI_ASSISTANT?.added && addonsSettings?.AI_ASSISTANT?.enabled,
  );
  const agentStatus = useFeatureAvailability('ai-agent');
  const aiUnavailable = !aiAddonEnabled || agentStatus === 'unavailable';

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'j' || e.key === 'J')) {
        e.preventDefault();
        if (e.shiftKey) {
          setMaximized((prev) => !prev);
        } else {
          setChatOpen((prev) => !prev);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setChatOpen, setMaximized]);

  const handleSend = useCallback(
    async (message: string, mcpPrompt?: string, mcpPromptArgs?: Record<string, string>) => {
      await sendChat(message, { mcpPrompt, mcpPromptArgs });
    },
    [sendChat],
  );

  const handlePendingChipConsumed = useCallback(() => {
    setPendingChip(null);
  }, [setPendingChip]);

  const handleHistoryBack = useCallback(() => {
    setHistoryView(false);
  }, [setHistoryView]);

  // Swipe-right-to-close gesture for the mobile panel. The touch must
  // start in the header band so that scrolling messages, dragging the
  // input, etc. never trigger a close.
  const touchStartRef = useRef<{ x: number; y: number; inHeader: boolean } | null>(null);
  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!isMobile) return;
      const t = e.touches[0];
      if (!t) return;
      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
      const localY = t.clientY - rect.top;
      touchStartRef.current = {
        x: t.clientX,
        y: t.clientY,
        inHeader: localY <= HEADER_ZONE_PX,
      };
    },
    [isMobile],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!isMobile) return;
      const start = touchStartRef.current;
      touchStartRef.current = null;
      if (!start || !start.inHeader) return;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - start.x;
      const dy = Math.abs(t.clientY - start.y);
      if (dx > SWIPE_CLOSE_THRESHOLD_PX && dx > dy) {
        setChatOpen(false);
      }
    },
    [isMobile, setChatOpen],
  );

  const showScrim = isMobile && isOpen;
  const showFab = isMobile && !isOpen;

  return (
    <>
      <div
        className={cn(styles.scrim, { [styles.scrimOpen]: showScrim })}
        onClick={() => setChatOpen(false)}
        aria-hidden={!showScrim}
      />
      <div
        className={cn(styles.sidebar, {
          [styles.open]: isOpen,
          [styles.maximized]: isMaximized,
        })}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <ChatHeader />
        {aiUnavailable ? (
          <div className={styles.unavailableNotice} data-test="chat-unavailable">
            <ChatAI />
            <h3>Not enabled</h3>
            <p>Get in touch with us to upgrade your account for this feature.</p>
          </div>
        ) : (
          <>
            <BudgetIndicator />
            {historyView ? (
              <ChatHistory onBack={handleHistoryBack} />
            ) : (
              <>
                <MessageList />
                <div className={styles.inputArea}>
                  <ChatInput
                    onSend={handleSend}
                    onStop={stopChat}
                    pendingChip={pendingChip}
                    onPendingChipConsumed={handlePendingChipConsumed}
                    disabled={!budgetAllowed}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
      {showFab && (
        <button
          type="button"
          className={styles.fab}
          onClick={() => setChatOpen(true)}
          aria-label="Open AI chat"
        >
          <ChatAI />
        </button>
      )}
    </>
  );
};
